/**
 * TavernBackdrop — UI-2 four-layer parallax tavern interior, mounted
 * behind the Room when the lobby is in a non-playing state. Renders:
 *
 *   layer 0 — far wall + hearth with a flickering ember glow
 *   layer 1 — mid-shelf silhouettes (mugs, lantern hooks, bottles)
 *   layer 2 — near table edge + walking-floor strip
 *   layer 3 — ambient dust motes (canvas-driven, sparse)
 *
 * Time-of-day: dusk → night → late night via Date.now() % cycleMs.
 * NPC pass-through: every ~90s a silhouette walks across the back.
 * Reduce-motion: static frame, no canvas, no NPC, no flicker.
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '@tt/motion';

interface Props {
  /** Show the backdrop only when the lobby is in these states. */
  active: boolean;
}

const TIME_CYCLE_MS = 1000 * 60 * 18; // ~18-min full cycle (dusk → late)
const NPC_INTERVAL_MS = 90_000;

export function TavernBackdrop({ active }: Props) {
  const reduce = prefersReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [npcSeed, setNpcSeed] = useState<{ id: number; kind: NpcKind } | null>(null);
  const [tod, setTod] = useState(timeOfDay());

  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const tick = () => { setTod(timeOfDay()); raf = window.requestAnimationFrame(tick); };
    if (!reduce) raf = window.requestAnimationFrame(tick);
    return () => { if (raf) window.cancelAnimationFrame(raf); };
  }, [active, reduce]);

  // Cycle NPCs every NPC_INTERVAL_MS.
  useEffect(() => {
    if (!active || reduce) return;
    let id = 0;
    const fire = () => {
      const kinds: NpcKind[] = ['keeper', 'cat', 'courier', 'spirit'];
      const kind = kinds[Math.floor(Math.random() * kinds.length)] ?? 'cat';
      id += 1;
      setNpcSeed({ id, kind });
    };
    fire();
    const handle = window.setInterval(fire, NPC_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [active, reduce]);

  // Dust motes — light canvas overlay.
  useEffect(() => {
    if (!active || reduce) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio ?? 1);
    const sync = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };
    sync();
    window.addEventListener('resize', sync);
    interface Mote { x: number; y: number; vx: number; vy: number; life: number; max: number; r: number }
    const motes: Mote[] = [];
    const targetCount = 60;
    function spawn(): Mote {
      return {
        x: Math.random() * canvas!.width,
        y: Math.random() * canvas!.height,
        vx: (Math.random() - 0.5) * 0.18 * dpr,
        vy: -Math.random() * 0.22 * dpr,
        life: 0,
        max: 4500 + Math.random() * 6000,
        r: (1 + Math.random() * 1.6) * dpr,
      };
    }
    let last = performance.now();
    let raf = 0;
    const draw = (now: number) => {
      const dt = Math.min(48, now - last);
      last = now;
      while (motes.length < targetCount) motes.push(spawn());
      ctx.clearRect(0, 0, canvas!.width, canvas!.height);
      for (const m of motes) {
        m.life += dt;
        m.x += m.vx * dt;
        m.y += m.vy * dt;
        const a = Math.max(0, Math.min(1, 1 - Math.abs((m.life / m.max) - 0.5) * 2)) * 0.85;
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 210, 130, ${a})`;
        ctx.shadowColor = 'rgba(255, 200, 100, 0.7)';
        ctx.shadowBlur = 6;
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      for (let i = motes.length - 1; i >= 0; i--) {
        if (motes[i]!.life > motes[i]!.max || motes[i]!.y < -10) motes.splice(i, 1);
      }
      raf = window.requestAnimationFrame(draw);
    };
    raf = window.requestAnimationFrame(draw);
    return () => { window.cancelAnimationFrame(raf); window.removeEventListener('resize', sync); };
  }, [active, reduce]);

  if (!active) return null;
  return (
    <div className={`tavern-backdrop tavern-tod-${tod}`} aria-hidden="true">
      <TavernInteriorSvg />
      {!reduce && <canvas ref={canvasRef} className="tavern-motes" />}
      {!reduce && npcSeed && <NpcWalker key={npcSeed.id} kind={npcSeed.kind} />}
    </div>
  );
}

/**
 * Full waiting-room tavern interior — wide-format SVG (1600×900) per
 * docs/ui/room-screens.md §9. Stone wall + plank wainscoting +
 * perspective floor + central hearth + right-side bar with bottles +
 * 4 hanging lanterns + 4 wall sconces + pendulum clock + dartboard +
 * mid-distance stools + framed picture. All ambient animation via
 * SVG SMIL `<animate>`; reduced-motion freezes flames + pendulum.
 */
function TavernInteriorSvg() {
  return (
    <svg
      className="tavern-svg"
      viewBox="0 0 1600 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="tvSky" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#1a0e1f" />
          <stop offset="40%" stopColor="#2a1a2a" />
          <stop offset="78%" stopColor="#3a2418" />
          <stop offset="100%" stopColor="#1a0e08" />
        </linearGradient>

        <pattern id="tvStone" x="0" y="0" width="160" height="80" patternUnits="userSpaceOnUse">
          <rect width="160" height="80" fill="#1f1820" />
          <rect x="0"   y="0"  width="76" height="38" fill="#2a2030" stroke="#0f0a14" strokeWidth="1.5" />
          <rect x="80"  y="0"  width="76" height="38" fill="#241a26" stroke="#0f0a14" strokeWidth="1.5" />
          <rect x="-40" y="42" width="76" height="38" fill="#2a2030" stroke="#0f0a14" strokeWidth="1.5" />
          <rect x="40"  y="42" width="76" height="38" fill="#241a26" stroke="#0f0a14" strokeWidth="1.5" />
          <rect x="120" y="42" width="76" height="38" fill="#2e2434" stroke="#0f0a14" strokeWidth="1.5" />
        </pattern>

        <pattern id="tvPlankWall" x="0" y="0" width="64" height="240" patternUnits="userSpaceOnUse">
          <rect width="64" height="240" fill="#2a1a10" />
          <rect x="0"  y="0" width="32" height="240" fill="#321e12" />
          <rect x="32" y="0" width="32" height="240" fill="#28180e" />
          <line x1="0"  y1="0" x2="0"  y2="240" stroke="#0a0604" strokeWidth="1.2" />
          <line x1="32" y1="0" x2="32" y2="240" stroke="#0a0604" strokeWidth="1.2" />
          <line x1="20" y1="0" x2="20" y2="240" stroke="#3a2410" strokeWidth="0.4" opacity="0.55" />
          <line x1="44" y1="0" x2="44" y2="240" stroke="#3a2410" strokeWidth="0.4" opacity="0.55" />
        </pattern>

        <pattern id="tvPlankFloor" x="0" y="0" width="240" height="56" patternUnits="userSpaceOnUse">
          <rect width="240" height="56" fill="#2a1a0e" />
          <rect x="0" y="0"  width="240" height="28" fill="#341f10" />
          <rect x="0" y="28" width="240" height="28" fill="#26170c" />
          <line x1="0" y1="0"  x2="240" y2="0"  stroke="#0a0604" strokeWidth="1.2" />
          <line x1="0" y1="28" x2="240" y2="28" stroke="#0a0604" strokeWidth="1.2" />
        </pattern>

        <radialGradient id="tvHearthGlow" cx="0.5" cy="0.7" r="0.7">
          <stop offset="0%"   stopColor="#ffd28a" stopOpacity="0.9" />
          <stop offset="40%"  stopColor="#ff8c42" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ff4a2a" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tvHearthHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#ff8c42" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#c25516" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tvLanternHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#ffb27a" stopOpacity="0.36" />
          <stop offset="100%" stopColor="#ff8c42" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tvSconceHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#ffb27a" stopOpacity="0.26" />
          <stop offset="100%" stopColor="#ff8c42" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="tvBottleG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2a4a2a" /><stop offset="100%" stopColor="#0a1a0a" />
        </linearGradient>
        <linearGradient id="tvBottleA" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#a87a3a" /><stop offset="100%" stopColor="#3a2410" />
        </linearGradient>
        <linearGradient id="tvBottleC" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5a4a4a" /><stop offset="100%" stopColor="#1a0e0a" />
        </linearGradient>
        <linearGradient id="tvOak" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5b3a1f" /><stop offset="60%" stopColor="#3a2410" /><stop offset="100%" stopColor="#1a0e08" />
        </linearGradient>
        <linearGradient id="tvTopFog" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="rgba(10,8,20,0.85)" />
          <stop offset="100%" stopColor="rgba(10,8,20,0)" />
        </linearGradient>

        <style>
          {`@media (prefers-reduced-motion: reduce) {
            .tavern-svg animate, .tavern-svg animateTransform { display: none; }
            .tavern-svg .tv-flame-core { opacity: 0.85 !important; }
            .tavern-svg .tv-flame-mid  { opacity: 0.7 !important; }
            .tavern-svg .tv-flame-out  { opacity: 0.5 !important; }
          }`}
        </style>
      </defs>

      {/* sky/wall */}
      <rect x="0" y="0" width="1600" height="900" fill="url(#tvSky)" />
      {/* stone wall back */}
      <rect x="0" y="0" width="1600" height="500" fill="url(#tvStone)" />
      <rect x="0" y="0" width="1600" height="500" fill="rgba(0,0,0,0.18)" />
      {/* plank wainscoting */}
      <rect x="0" y="380" width="1600" height="240" fill="url(#tvPlankWall)" />
      <rect x="0" y="376" width="1600" height="8" fill="#5b3a1f" />
      <rect x="0" y="374" width="1600" height="2" fill="#7a4a28" />
      <rect x="0" y="612" width="1600" height="6" fill="#3a2410" />
      <rect x="0" y="610" width="1600" height="2" fill="#5b3a1f" />

      {/* floor with perspective hint */}
      <rect x="0" y="620" width="1600" height="280" fill="url(#tvPlankFloor)" />
      <rect x="0" y="620" width="1600" height="40" fill="rgba(0,0,0,0.35)" />
      <rect x="0" y="660" width="1600" height="32" fill="rgba(0,0,0,0.18)" />
      <rect x="0" y="692" width="1600" height="22" fill="rgba(0,0,0,0.08)" />

      {/* HEARTH (center) — toned down opacities so content stacked
          on top of the page reads cleanly. The hearth still gives
          ambient warmth without becoming a giant fireball. */}
      <g className="tv-hearth" opacity="0.6">
        <ellipse cx="800" cy="720" rx="280" ry="80" fill="url(#tvHearthHalo)" opacity="0.55">
          <animate attributeName="rx" values="280;310;290;280" dur="4s" repeatCount="indefinite" />
        </ellipse>
        <path d="M 690 620 L 690 460 Q 690 320 800 320 Q 910 320 910 460 L 910 620 Z" fill="#1a1218" stroke="#0a0608" strokeWidth="3" />
        <rect x="668" y="304" width="264" height="22" fill="#3a2410" stroke="#1a0e08" strokeWidth="1.5" />
        <rect x="664" y="298" width="272" height="8"  fill="#5b3a1f" />
        <rect x="730" y="430" width="140" height="190" fill="#080406" />
        <ellipse cx="770" cy="600" rx="32" ry="9" fill="#3a2410" />
        <ellipse cx="830" cy="600" rx="32" ry="9" fill="#3a2410" />
        <ellipse cx="800" cy="588" rx="38" ry="9" fill="#5b3a1f" />
        <ellipse cx="800" cy="595" rx="44" ry="9" fill="#ff4a2a" opacity="0.45">
          <animate attributeName="opacity" values="0.4;0.6;0.45" dur="1.7s" repeatCount="indefinite" />
        </ellipse>
        <ellipse className="tv-flame-out" cx="800" cy="540" rx="48" ry="70" fill="#ff8c42" opacity="0.32">
          <animate attributeName="opacity" values="0.26;0.42;0.32" dur="1.3s" repeatCount="indefinite" />
        </ellipse>
        <ellipse className="tv-flame-mid" cx="800" cy="540" rx="30" ry="58" fill="#ffb27a" opacity="0.45">
          <animate attributeName="opacity" values="0.38;0.58;0.45" dur="0.9s" repeatCount="indefinite" />
        </ellipse>
        <ellipse className="tv-flame-core" cx="800" cy="548" rx="16" ry="42" fill="#fff7c2" opacity="0.55">
          <animate attributeName="opacity" values="0.45;0.7;0.55;0.5" dur="0.6s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="800" cy="540" rx="60" ry="80" fill="url(#tvHearthGlow)" opacity="0.6" />
      </g>

      {/* BAR (right side) */}
      <g className="tv-bar">
        <rect x="1280" y="380" width="320" height="220" fill="#2a1a10" stroke="#0a0604" strokeWidth="2" />
        <line x1="1280" y1="450" x2="1600" y2="450" stroke="#5b3a1f" strokeWidth="2" />
        <line x1="1280" y1="520" x2="1600" y2="520" stroke="#5b3a1f" strokeWidth="2" />
        {[1300, 1326, 1350, 1376, 1402, 1428, 1454, 1480, 1506, 1532, 1558, 1582].map((x, i) => (
          <rect key={`tb-r1-${i}`} x={x} y={i % 2 ? 396 : 392} width="14" height={i % 2 ? 50 : 54}
            fill={`url(#${['tvBottleG','tvBottleA','tvBottleC'][i % 3]})`} />
        ))}
        {[1300, 1322, 1344, 1366, 1388, 1410, 1432, 1454, 1476, 1498, 1520, 1542, 1564, 1586].map((x, i) => (
          <rect key={`tb-r2-${i}`} x={x} y={i % 2 ? 466 : 462} width="14" height={i % 2 ? 48 : 52}
            fill={`url(#${['tvBottleA','tvBottleG','tvBottleC'][i % 3]})`} />
        ))}
        {/* Cask */}
        <rect x="1380" y="538" width="80" height="58" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
        <ellipse cx="1420" cy="567" rx="40" ry="29" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
        <line x1="1380" y1="552" x2="1460" y2="552" stroke="#3a2410" strokeWidth="2" />
        <line x1="1380" y1="582" x2="1460" y2="582" stroke="#3a2410" strokeWidth="2" />

        {/* Bar counter */}
        <rect x="1240" y="600" width="360" height="22" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="2" />
        <rect x="1240" y="600" width="360" height="3"  fill="#a87a3a" opacity="0.6" />
        <rect x="1240" y="622" width="360" height="200" fill="url(#tvOak)" stroke="#1a0e08" strokeWidth="2" />
        <rect x="1240" y="800" width="360" height="22"  fill="#1a0e08" />

        {/* Tankards */}
        <rect x="1280" y="572" width="26" height="28" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1.5" />
        <rect x="1280" y="572" width="26" height="3" fill="#fff7c2" opacity="0.85" />
        <rect x="1306" y="576" width="6" height="20" fill="none" stroke="#1a0e08" strokeWidth="1.5" />
        <rect x="1360" y="574" width="22" height="26" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1.5" />

        {/* Bar candle */}
        <rect x="1538" y="572" width="6" height="22" fill="#f3e3c2" />
        <rect x="1532" y="592" width="18" height="4" fill="#a87a3a" />
        <ellipse cx="1541" cy="568" rx="3" ry="6" fill="#ffb27a" opacity="0.8">
          <animate attributeName="opacity" values="0.55;0.95;0.7" dur="1.1s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="1541" cy="569" rx="2" ry="4" fill="#fff7c2" opacity="0.9" />
        <ellipse cx="1541" cy="568" rx="22" ry="22" fill="url(#tvSconceHalo)" />
      </g>

      {/* HANGING LANTERNS (4 across the top) */}
      {[
        { x: 240, y: 200, dur: 1.2, depth: 30 },
        { x: 560, y: 180, dur: 1.7, depth: 26 },
        { x: 1040, y: 180, dur: 1.5, depth: 26 },
        { x: 1360, y: 200, dur: 2.1, depth: 30 },
      ].map((l, i) => (
        <g key={`tv-lan-${i}`} className="tv-lantern">
          <line x1={l.x} y1="0" x2={l.x} y2={l.y - 36} stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx={l.x} cy={l.y} rx="22" ry={l.depth} fill="#1a0e08" stroke="#3a2410" strokeWidth="2" />
          <rect x={l.x - 16} y={l.y - 28} width="32" height="6" fill="#3a2410" />
          <rect x={l.x - 10} y={l.y + 24} width="20" height="4" fill="#3a2410" />
          <ellipse cx={l.x} cy={l.y} rx="14" ry={l.depth - 8} fill="#ff8c42" opacity="0.65">
            <animate attributeName="opacity" values="0.5;0.95;0.65" dur={`${l.dur}s`} repeatCount="indefinite" />
          </ellipse>
          <ellipse cx={l.x} cy={l.y + 2} rx="8" ry={l.depth - 14} fill="#fff7c2" opacity="0.9" />
          <ellipse cx={l.x} cy={l.y} rx="80" ry="80" fill="url(#tvLanternHalo)" />
        </g>
      ))}

      {/* WALL SCONCES (4 along the back wall, between hearth and edges) */}
      {[
        { x: 200, dur: 1.4 },
        { x: 600, dur: 1.5 },
        { x: 1000, dur: 1.3 },
        { x: 1200, dur: 1.6 },
      ].map((s, i) => (
        <g key={`tv-sc-${i}`} className="tv-sconce">
          <rect x={s.x - 10} y="384" width="20" height="8" fill="#1a0e08" />
          <rect x={s.x - 4}  y="392" width="8"  height="14" fill="#1a0e08" />
          <rect x={s.x - 6}  y="406" width="12" height="6" fill="#3a2410" />
          <rect x={s.x - 3}  y="372" width="6"  height="14" fill="#f3e3c2" />
          <ellipse cx={s.x} cy="372" rx="3" ry="6" fill="#ffb27a" opacity="0.8">
            <animate attributeName="opacity" values="0.55;0.95;0.7" dur={`${s.dur}s`} repeatCount="indefinite" />
            <animate attributeName="ry" values="6;7.5;5.5;6" dur={`${s.dur}s`} repeatCount="indefinite" />
          </ellipse>
          <ellipse cx={s.x} cy="374" rx="2" ry="4" fill="#fff7c2" opacity="0.9" />
          <ellipse cx={s.x} cy="374" rx="40" ry="40" fill="url(#tvSconceHalo)" />
        </g>
      ))}

      {/* DECOR — pendulum clock + framed picture + dartboard + deer skull */}
      <g className="tv-decor">
        {/* Pendulum clock — left of hearth */}
        <rect x="80" y="448" width="56" height="120" fill="#3a2410" stroke="#1a0e08" strokeWidth="2" />
        <circle cx="108" cy="478" r="22" fill="#f3e3c2" stroke="#1a0e08" strokeWidth="2" />
        <circle cx="108" cy="478" r="20" fill="none" stroke="#a87a3a" strokeWidth="1" />
        <line x1="108" y1="478" x2="108" y2="464" stroke="#1a0e08" strokeWidth="2" />
        <line x1="108" y1="478" x2="118" y2="478" stroke="#1a0e08" strokeWidth="1.5" />
        <rect x="94" y="510" width="28" height="48" fill="#0a0604" />
        <g transform="translate(108 510)">
          <line x1="0" y1="0" x2="0" y2="36" stroke="#3a2410" strokeWidth="1.5" />
          <circle cx="0" cy="42" r="6" fill="#a87a3a" stroke="#1a0e08" strokeWidth="1" />
          <animateTransform attributeName="transform" type="rotate" values="-8;8;-8" dur="2s" additive="sum" repeatCount="indefinite" />
        </g>

        {/* Framed picture — between hearth and bar */}
        <rect x="1100" y="220" width="68" height="84" fill="url(#tvOak)" stroke="#1a0e08" strokeWidth="2" />
        <rect x="1108" y="228" width="52" height="68" fill="#3a2a40" />
        <ellipse cx="1134" cy="266" rx="14" ry="14" fill="#1a1218" />

        {/* Dartboard — right of bar / on back wall */}
        <circle cx="1480" cy="240" r="34" fill="#1a1218" stroke="#3a2410" strokeWidth="2" />
        <circle cx="1480" cy="240" r="28" fill="#7a1828" />
        <circle cx="1480" cy="240" r="22" fill="#cab8a0" />
        <circle cx="1480" cy="240" r="14" fill="#7a1828" />
        <circle cx="1480" cy="240" r="6"  fill="#cab8a0" />
        <circle cx="1480" cy="240" r="2"  fill="#1a0e08" />
        {/* Spokes */}
        {[0, 30, 60, 90, 120, 150].map((deg) => (
          <line key={deg} x1="1480" y1="240" x2={1480 + 28 * Math.cos((deg * Math.PI) / 180)} y2={240 + 28 * Math.sin((deg * Math.PI) / 180)}
            stroke="#1a0e08" strokeWidth="0.8" opacity="0.4" />
        ))}

        {/* Deer skull above hearth */}
        <ellipse cx="800" cy="226" rx="34" ry="26" fill="#e6d6c0" stroke="#1a0e08" strokeWidth="1.5" />
        <ellipse cx="786" cy="222" rx="4" ry="6" fill="#1a0e08" />
        <ellipse cx="814" cy="222" rx="4" ry="6" fill="#1a0e08" />
        <path d="M 774 218 Q 750 180 740 160 M 748 174 L 732 168" fill="none" stroke="#e6d6c0" strokeWidth="2.5" />
        <path d="M 826 218 Q 850 180 860 160 M 852 174 L 868 168" fill="none" stroke="#e6d6c0" strokeWidth="2.5" />

        {/* Mid-distance stools (small silhouettes around the room) */}
        {[
          { x: 360, y: 700 }, { x: 460, y: 720 },
          { x: 1180, y: 720 }, { x: 1080, y: 700 },
        ].map((s, i) => (
          <g key={`tv-stool-${i}`}>
            <ellipse cx={s.x} cy={s.y} rx="22" ry="6" fill="#1a0e08" opacity="0.6" />
            <rect x={s.x - 16} y={s.y - 30} width="32" height="6" fill="#3a2410" />
            <rect x={s.x - 14} y={s.y - 24} width="3" height="22" fill="#1a0e08" />
            <rect x={s.x + 11} y={s.y - 24} width="3" height="22" fill="#1a0e08" />
          </g>
        ))}
      </g>

      {/* TOP FOG */}
      <rect x="0" y="0" width="1600" height="240" fill="url(#tvTopFog)" />
    </svg>
  );
}

type NpcKind = 'keeper' | 'cat' | 'courier' | 'spirit';

function NpcWalker({ kind }: { kind: NpcKind }) {
  return (
    <div className={`tavern-npc tavern-npc-${kind}`}>
      <span className="tavern-npc-glyph">{npcGlyph(kind)}</span>
    </div>
  );
}
function npcGlyph(kind: NpcKind): string {
  switch (kind) {
    case 'keeper':  return '🍻';
    case 'cat':     return '🐈';
    case 'courier': return '✉';
    case 'spirit':  return '✦';
  }
}

function timeOfDay(): 'dusk' | 'night' | 'late' {
  const t = (Date.now() % TIME_CYCLE_MS) / TIME_CYCLE_MS;
  if (t < 0.33) return 'dusk';
  if (t < 0.66) return 'night';
  return 'late';
}
